<template>
    <div class="page-container">
        <CrmPageHeader
            title="Membresías"
            subtitle="Gestión de usuarios y planes de membresía"
            icon="fas fa-id-card"
        >
            <span class="badge bg-primary bg-gradient px-3 py-2" style="font-size:0.78rem">
                <i class="fas fa-users me-1"></i>
                {{ memberships.total || 0 }} usuarios
            </span>
            <soft-button variant="gradient" size="sm" @click="showModalForm()">
                <i class="fas fa-plus me-1"></i> Agregar
            </soft-button>
            <soft-button variant="outline" size="sm" @click="exportMembresia()" :loading="isLoading">
                <i class="fas fa-file-export me-1"></i> Exportar
            </soft-button>
        </CrmPageHeader>

        <div class="row">
            <!-- Alert Section -->
            <div class="col-12">
                <soft-alert v-if="alertTable.state" class="mb-3">
                    {{ alertTable.message }}
                </soft-alert>
            </div>

            <!-- Main Table Section -->
            <div class="col-12">
                <table-component :headers="headers" :items="memberships.data" :spinner="spinner" name="Membresías">
                    <!-- Header Actions -->
                    <template #headers>
                        <filters v-model:userId="userId" v-model:nameAgent="nameAgent" v-model:email="email"
                            v-model:cedula="cedula" v-model:role="role"
                            v-model:membership="membership" :roles="roles" :memberships_plans="memberships_plans"
                            @search="applyFilters" @clear="cleanFilter" @refresh="getMemberships(1)">
                        </filters>
                    </template>

                    <!-- Column Rendering -->
                    <template #columnRender="{ header, value }">
                        <!-- ID Column -->
                        <span v-if="header.field === 'id'" class="fw-bold text-primary">
                            #{{ value.id }}
                        </span>

                        <!-- Name Column -->
                        <span v-else-if="header.field === 'primer_nombre'" class="text-truncate">
                            {{ fullName(value.userdata) || "Sin nombre" }}
                        </span>

                        <!-- Email Column -->
                        <span v-else-if="header.field === 'email'" class="text-truncate">
                            {{ value.email || "N/A" }}
                        </span>

                        <!-- Status Column -->
                        <span v-else-if="header.field === 'estado_user'">
                            <select v-model="value.estado_user"
                                @change="toggleEstadoUser({ id: value.id, estado_user: value.estado_user })"
                                class="form-select form-select-sm status-select"
                                :class="value.estado_user === 'activo' ? 'status-active' : 'status-inactive'"
                                :disabled="isLoading" style="width: 120px;">
                                <option value="activo">Activo</option>
                                <option value="inactivo">Inactivo</option>
                            </select>
                        </span>

                        <!-- Users Permission Count -->
                        <span v-else-if="header.field === 'permission_count_usuarios'">
                            <input type="number" min="0" v-model="value.permission_count_usuarios"
                                @change="updateCountUser({ id: value.id, amount: value.permission_count_usuarios, plans_permission: 'usuarios' })"
                                class="form-control form-control-sm text-center" :disabled="isLoading"
                                style="width: 80px;" placeholder="0" />
                        </span>

                        <!-- Properties Permission Count -->
                        <span v-else-if="header.field === 'permission_count_inmuebles'">
                            <input type="number" min="0" v-model="value.permission_count_inmuebles"
                                @change="updateCountUser({ id: value.id, amount: value.permission_count_inmuebles, plans_permission: 'inmuebles' })"
                                class="form-control form-control-sm text-center" :disabled="isLoading"
                                style="width: 80px;" placeholder="0" />
                        </span>

                        <!-- Payment Plan -->
                        <span v-else-if="header.field === 'payment_plan_users'">
                            <soft-badge v-if="value.payment_plan_users" color="success" variant="gradient" size="sm">
                                {{ value.payment_plan_users.plans.code }}
                            </soft-badge>
                            <span v-else class="text-muted small">Sin plan</span>
                        </span>

                        <!-- User Data Action -->
                        <span v-else-if="header.field === 'userdata'">
                            <a v-if="value.userdata != null" href="#" class="table-action-link"
                                @click.prevent="showModalUserdata(value)" title="Ver detalles">
                                <i class="fas fa-eye"></i>
                            </a>
                            <span v-else class="text-muted small">—</span>
                        </span>

                        <!-- Child Users Count -->
                        <span v-else-if="header.field === 'child_users_count'">
                            <a v-if="value.child_users_count > 0" href="#"
                                class="badge bg-info text-white text-decoration-none"
                                @click.prevent="showModalChildren(value)"
                                title="Ver usuarios hijos">
                                <i class="fas fa-users me-1"></i>
                                {{ value.child_users_count }}
                            </a>
                            <span v-else class="text-muted small">0</span>
                        </span>

                        <!-- Properties Count -->
                        <span v-else-if="header.field === 'inmuebles_count'" class="fw-semibold text-dark">
                            {{ value.inmuebles_count || 0 }}
                        </span>

                        <!-- User Role -->
                        <span v-else-if="header.field === 'roles_users'">
                            <span v-if="value.roles_users" class="badge bg-light text-dark border">
                                {{ value.roles_users.role.nombre }}
                            </span>
                            <span v-else class="text-muted small">—</span>
                        </span>

                        <!-- Actions Column -->
                        <span v-else-if="header.field === 'actions'" class="d-flex gap-1">
                            <a href="#" class="table-action-link" @click.prevent="showModalForm(value)"
                                :class="{ disabled: isLoading }" title="Editar">
                                <i class="fas fa-edit"></i>
                            </a>
                            <a href="#" class="table-action-link"
                                @click.prevent="impersonateUser(value.id, fullName(value.userdata) || value.email)"
                                :class="{ disabled: isLoading }" title="Entrar como usuario">
                                <i class="fas fa-user-secret"></i>
                            </a>
                            <a href="#" class="table-action-link"
                                @click.prevent="openChangePassword(value)"
                                :class="{ disabled: isLoading }"
                                :title="canChangeEmail ? 'Cambiar correo / contraseña' : 'Cambiar contraseña'">
                                <i class="fas fa-lock"></i>
                            </a>
                        </span>
                    </template>

                    <!-- Pagination -->
                    <template v-slot:pagination>
                        <div class="d-flex justify-content-center mt-3">
                            <base-pagination :current-page="memberships.current_page || 1"
                                :pageCount="memberships.last_page || 1" @nextPage="pageChangeHandle('next')"
                                @previousPage="pageChangeHandle('previous')" @loadPage="pageChangeHandle" />
                        </div>
                    </template>
                </table-component>

                <!-- User Details Modal -->
                <soft-modal @close="toggleModal" :modalActive="modalActive" v-if="modalActive" size="lg">
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-user-circle me-2 text-primary"></i>
                            Detalles del Usuario
                        </h5>
                    </template>
                    <detailUsers :userdata="userdata" :empresa="empresaData" />
                </soft-modal>

                <!-- User Form Modal -->
                <soft-modal @close="toggleModalForm" :modalActive="modalActiveForm" v-if="modalActiveForm" size="xl">
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-user-edit me-2 text-primary"></i>
                            {{ userdata ? 'Editar Usuario' : 'Nuevo Usuario' }}
                        </h5>
                    </template>
                    <form-users :userdata="userdata" @close="closeModal" />
                </soft-modal>

                <!-- Credential Modal -->
                <soft-modal @close="toggleModalCredential" :modalActive="modalActiveCredential"
                    v-if="modalActiveCredential" size="lg">
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-key me-2 text-primary"></i>
                            Asignar Credencial
                        </h5>
                    </template>
                    <div class="p-3">
                        <label for="credentialMercadolibre" class="form-label fw-semibold">
                            Credenciales de Mercado Libre
                        </label>
                        <select class="form-select" id="credentialMercadolibre"
                            v-model="credentialMercadolibreForm.credentialId"
                            @change="addCredentialMercadolibre()">
                            <option value="" disabled>Selecciona una credencial</option>
                            <option v-for="credential in credentialMercadolibre" :key="credential.id"
                                :value="credential.id">
                                {{ credential.email }} — {{ credential.client_id }}
                            </option>
                        </select>

                        <div v-if="userdata?.mercadolibre_credentials?.length > 0" class="mt-3">
                            <small class="text-muted fw-semibold">Credencial actual:</small>
                            <div v-for="cred in userdata.mercadolibre_credentials" :key="cred.id"
                                class="badge bg-light text-dark border d-block mt-1 text-start">
                                {{ cred.email }}
                            </div>
                        </div>
                    </div>
                </soft-modal>

                <!-- Child Users Modal -->
                <soft-modal @close="toggleModalChildren" :modalActive="modalActiveChildren"
                    v-if="modalActiveChildren" size="xl">
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-users me-2 text-info"></i>
                            Usuarios Hijos de {{ fullName(childParentUser?.userdata) || childParentUser?.email || '' }}
                        </h5>
                    </template>
                    <div class="p-3">
                        <!-- Filters -->
                        <div class="row g-2 mb-3">
                            <div class="col-md-3">
                                <input type="text" class="form-control form-control-sm" placeholder="Buscar por nombre"
                                    v-model="childFilters.nameAgent" @keyup.enter="applyChildFilters" />
                            </div>
                            <div class="col-md-3">
                                <input type="email" class="form-control form-control-sm" placeholder="Buscar por email"
                                    v-model="childFilters.email" @keyup.enter="applyChildFilters" />
                            </div>
                            <div class="col-md-3">
                                <input type="text" class="form-control form-control-sm" placeholder="Buscar por rol"
                                    v-model="childFilters.role" @keyup.enter="applyChildFilters" />
                            </div>
                            <div class="col-md-3">
                                <soft-button variant="gradient" size="sm" class="w-100" @click="applyChildFilters">
                                    <i class="fas fa-search me-1"></i> Buscar
                                </soft-button>
                            </div>
                        </div>

                        <!-- Table -->
                        <div class="table-responsive">
                            <div v-if="spinnerChildren" class="text-center py-4">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">Cargando...</span>
                                </div>
                            </div>
                            <table v-else class="table table-hover align-middle mb-0">
                                <thead class="bg-light">
                                    <tr>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Código</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Nombre</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Email</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Rol</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Plan</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Inmuebles Creados</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="child in childUsers.data" :key="child.id">
                                        <td class="fw-bold text-primary">#{{ child.id }}</td>
                                        <td>{{ fullName(child.userdata) || 'Sin nombre' }}</td>
                                        <td>{{ child.email || 'N/A' }}</td>
                                        <td>
                                            <span v-if="child.roles_users" class="badge bg-light text-dark border">
                                                {{ child.roles_users.role.nombre }}
                                            </span>
                                            <span v-else class="text-muted small">—</span>
                                        </td>
                                        <td>
                                            <soft-badge v-if="child.payment_plan_users" color="success" variant="gradient" size="sm">
                                                {{ child.payment_plan_users.plans.code }}
                                            </soft-badge>
                                            <span v-else class="text-muted small">Sin plan</span>
                                        </td>
                                        <td class="fw-semibold">{{ child.inmuebles_created_count || 0 }}</td>
                                    </tr>
                                    <tr v-if="!childUsers.data || childUsers.data.length === 0">
                                        <td colspan="6" class="text-center text-muted py-4">
                                            No se encontraron usuarios hijos
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Pagination -->
                        <div class="d-flex justify-content-between align-items-center mt-3">
                            <small class="text-muted">Total: {{ childUsers.total || 0 }} usuarios</small>
                            <base-pagination v-if="childUsers.last_page > 1"
                                :current-page="childUsers.current_page || 1"
                                :pageCount="childUsers.last_page || 1"
                                @nextPage="childPageChangeHandle('next')"
                                @previousPage="childPageChangeHandle('previous')"
                                @loadPage="childPageChangeHandle" />
                        </div>
                    </div>
                </soft-modal>

                <!-- Change Password Modal -->
                <soft-modal @close="closeChangePassword" :modalActive="modalActiveChangePassword"
                    v-if="modalActiveChangePassword" size="sm">
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-lock me-2 text-warning"></i>
                            {{ canChangeEmail ? 'Cambiar correo / contraseña' : 'Cambiar contraseña' }}
                            — {{ fullName(passwordUser?.userdata) || passwordUser?.email }}
                        </h5>
                    </template>
                    <div class="p-3">
                        <template v-if="canChangeEmail">
                            <label class="form-label fw-semibold text-sm mb-1">Correo</label>
                            <input
                                type="email"
                                class="form-control form-control-sm pwd-input mb-3"
                                v-model="newEmail"
                                placeholder="correo@dominio.com"
                                @keyup.enter="submitChangePassword(msg => showAlert(msg, 2000))"
                            />
                        </template>
                        <label class="form-label fw-semibold text-sm mb-1">
                            Nueva contraseña
                            <span v-if="canChangeEmail" class="text-muted fw-normal">(opcional)</span>
                        </label>
                        <div class="pwd-field mb-1">
                            <input
                                :type="showPassword ? 'text' : 'password'"
                                class="form-control form-control-sm pwd-input"
                                v-model="newPassword"
                                placeholder="Mínimo 6 caracteres"
                                @keyup.enter="submitChangePassword(msg => showAlert(msg, 2000))"
                                autofocus
                            />
                            <button class="pwd-toggle" type="button" @click="showPassword = !showPassword"
                                tabindex="-1">
                                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                            </button>
                        </div>
                        <div v-if="passwordError" class="text-danger" style="font-size:0.75rem">
                            <i class="fas fa-exclamation-circle me-1"></i>{{ passwordError }}
                        </div>
                        <div class="d-flex gap-2 justify-content-end mt-3">
                            <soft-button variant="outline" size="sm" @click="closeChangePassword">
                                Cancelar
                            </soft-button>
                            <soft-button variant="gradient" size="sm"
                                :loading="isLoadingPassword"
                                @click="submitChangePassword(msg => showAlert(msg, 2000))">
                                <i class="fas fa-check me-1"></i> Guardar
                            </soft-button>
                        </div>
                    </div>
                </soft-modal>

                <!-- Loading Overlay -->
                <div v-if="isLoading" class="loading-overlay">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="js">
import tableComponent from "@/components/tableComponent.vue";
import CrmPageHeader from "@/components/CrmPageHeader.vue";
import SoftBadge from "@/components/SoftBadge.vue";
import SoftModal from '@/components/SoftModal.vue'
import SoftButton from '@/components/SoftButton.vue'
import formUsers from "./components/formUsers.vue";
import detailUsers from "./components/detailUsers.vue";
import filters from "./components/filters.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import BasePagination from "@/components/pagination/BasePagination.vue";
import { useMembership, fullName } from "./useMembership.js";
import { useImpersonate } from "@/composables/useImpersonate.js";
import { useChangePassword } from "@/composables/useChangePassword.js";

export default {
    emits: ["close-modal"],
    name: "memberships",
    components: {
        tableComponent, SoftBadge, SoftModal, SoftButton,
        BasePagination, formUsers, filters, detailUsers, SoftAlert,
        CrmPageHeader
    },
    setup() {
        const { impersonateUser } = useImpersonate();
        const {
            modalActive: modalActiveChangePassword,
            isLoading: isLoadingPassword,
            newPassword,
            newEmail,
            showPassword,
            errorMessage: passwordError,
            selectedUser: passwordUser,
            canChangeEmail,
            openModal: openChangePassword,
            closeModal: closeChangePassword,
            submitChangePassword,
        } = useChangePassword();

        const {
            headers, userdata, empresaData,
            modalActive, modalActiveForm, modalActiveCredential,
            spinner, isLoading, alertTable,
            nameAgent, email, role, membership, userId, cedula,
            genres, countries, states, cities, zones, documentType,
            roles, memberships_plans, memberships,
            showModalCredential, addCredentialMercadolibre,
            getHelpers, getStates, getCities, getZones,
            getMemberships, getCredentialMercadolibre,
            credentialMercadolibre, credentialMercadolibreForm,
            updateCountUser, exportMembresia, toggleEstadoUser,
            cleanFilter, applyFilters, pageChangeHandle,
            showModalUserdata, showModalForm,
            toggleModal, toggleModalForm, toggleModalCredential, closeModal, showAlert,
            // Child users
            modalActiveChildren, childUsers, childParentUser, childFilters, spinnerChildren,
            showModalChildren, toggleModalChildren, applyChildFilters, childPageChangeHandle
        } = useMembership()

        getMemberships()
        getHelpers()
        getCredentialMercadolibre()

        return {
            fullName,
            headers, userdata, empresaData,
            modalActive, modalActiveForm, modalActiveCredential,
            spinner, isLoading, alertTable,
            nameAgent, email, role, membership, userId, cedula,
            showModalCredential,
            genres, countries, states, cities, zones, documentType,
            roles, memberships_plans, memberships,
            getStates, getCities, getZones,
            getMemberships, getCredentialMercadolibre,
            credentialMercadolibre, credentialMercadolibreForm,
            addCredentialMercadolibre,
            updateCountUser, exportMembresia, toggleEstadoUser,
            cleanFilter, applyFilters, pageChangeHandle,
            showModalUserdata, showModalForm,
            toggleModal, toggleModalForm, toggleModalCredential, closeModal, showAlert,
            // Child users
            modalActiveChildren, childUsers, childParentUser, childFilters, spinnerChildren,
            showModalChildren, toggleModalChildren, applyChildFilters, childPageChangeHandle,
            // Impersonation
            impersonateUser,
            // Change password
            modalActiveChangePassword, isLoadingPassword, newPassword, newEmail, showPassword,
            passwordError, passwordUser, canChangeEmail, openChangePassword, closeChangePassword, submitChangePassword,
        }
    }
};
</script>

<style lang="scss" scoped>
.text-truncate {
    max-width: 150px;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
}

.status-select {
    cursor: pointer;
    border-radius: 6px;
    font-size: 0.78rem;
    font-weight: 500;

    &.status-active {
        border-color: #10B981 !important;
        color: #065F46 !important;
        background: #ECFDF5 !important;
    }

    &.status-inactive {
        border-color: #F87171 !important;
        color: #991B1B !important;
        background: #FEF2F2 !important;
    }
}

.table-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    color: #64748B;
    transition: all 0.15s ease;
    text-decoration: none;

    &:hover {
        background: #EFF6FF;
        color: #2563EB;
    }

    &.disabled {
        opacity: 0.4;
        pointer-events: none;
    }
}

/* Password field */
.pwd-field {
    position: relative;
}

.pwd-input {
    padding-right: 36px;
    border-radius: 8px !important;
    font-size: 0.82rem;
}

.pwd-toggle {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 0;
    color: #94A3B8;
    font-size: 0.8rem;
    cursor: pointer;
    line-height: 1;
    transition: color 0.15s ease;

    &:hover {
        color: #334155;
    }
}

@media (max-width: 768px) {
    .text-truncate { max-width: 100px; }
}
</style>
